/**
 * StaffNotation Component
 * -----------------------
 * Renders musical notation on a WHITE background (like real sheet music)
 * using VexFlow with:
 *  - Correct stem directions (up below middle line, down above)
 *  - Proper voice/formatter for exact beat-filling
 *  - Dotted note support
 *  - Accidental rendering
 *  - Color-coded note states (current = blue, correct = green, wrong = red)
 */

import { useEffect, useRef, useCallback } from "react";
import { Renderer, Stave, StaveConnector, StaveNote, StaveTie, Voice, Formatter, Accidental, Dot, type RenderContext } from "vexflow";
import type { Exercise, ExerciseNote } from "@/lib/exerciseGenerator";
import { midiToVexFlowKey, getVexFlowKeySignature, getStemDirection, getExplicitAccidental } from "@/lib/exerciseGenerator";

interface StaffNotationProps {
  exercise: Exercise | null;
  currentNoteIndex: number;
  noteStates: Map<number, "correct" | "wrong" | "pending">;
  clef: "treble" | "bass" | "both";
}

interface TieState {
  pending: StaveNote | null;
  pendingRow: number;
}

interface TiePair {
  first?: StaveNote;
  last?: StaveNote;
}

// Standard sheet-music colors on white background
const COLORS = {
  staff: "#333333",
  note: "#1a1a1a",
  current: "#1565C0",    // strong blue
  correct: "#2E7D32",    // forest green
  wrong: "#C62828",      // deep red
  background: "#ffffff",
};

// Ties must be drawn after their notes have been formatted and drawn.
function drawTies(ties: TiePair[], context: RenderContext) {
  for (const tie of ties) {
    if (!tie.first && !tie.last) continue;
    try {
      new StaveTie({
        first_note: tie.first,
        last_note: tie.last,
        first_indices: [0],
        last_indices: [0],
      }).setContext(context).draw();
    } catch {
      // Skip un-renderable ties rather than failing the whole render
    }
  }
}

export default function StaffNotation({
  exercise,
  currentNoteIndex,
  noteStates,
  clef,
}: StaffNotationProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const buildVexFlowNotes = useCallback((
    notes: ExerciseNote[],
    noteClef: "treble" | "bass",
    keySignature: Exercise["keySignature"],
    noteIndexRef: { value: number },
    tieState: TieState,
    ties: TiePair[],
    row: number
  ): StaveNote[] => {
    const vfNotes: StaveNote[] = [];

    for (const note of notes) {
      const key = note.rest
        ? (noteClef === "treble" ? "b/4" : "d/3")
        : midiToVexFlowKey(note.midi, keySignature);

      const durationStr = note.rest ? `${note.duration}r` : note.duration;
      const stemDir = note.rest ? 1 : getStemDirection(note.midi, noteClef);

      const staveNote = new StaveNote({
        keys: [key],
        duration: durationStr,
        clef: noteClef,
        stem_direction: stemDir,
      });

      if (note.dotted) {
        Dot.buildAndAttach([staveNote], { all: true });
      }

      if (!note.rest) {
        const keyStr = midiToVexFlowKey(note.midi, keySignature);
        const noteName = keyStr.split("/")[0];
        const explicit = getExplicitAccidental(noteName, keySignature);
        if (explicit) {
          staveNote.addModifier(new Accidental(explicit), 0);
        }
      }

      let color = COLORS.note;
      if (!note.rest) {
        // Tied continuations share the playable index (and color) of the note they extend.
        const playableIdx = note.tieStop ? Math.max(0, noteIndexRef.value - 1) : noteIndexRef.value;
        const state = noteStates.get(playableIdx);
        if (playableIdx === currentNoteIndex) {
          color = COLORS.current;
        } else if (state === "correct") {
          color = COLORS.correct;
        } else if (state === "wrong") {
          color = COLORS.wrong;
        }
        if (!note.tieStop) noteIndexRef.value += 1;
      }

      staveNote.setStyle({ fillStyle: color, strokeStyle: color });
      staveNote.setStemStyle({ fillStyle: color, strokeStyle: color });
      // Noteheads and ledger lines need explicit styles: without a fill
      // attribute they inherit the page's CSS text color (light gray on the
      // dark theme), which washed the notes out on the white sheet.
      staveNote.setKeyStyle(0, { fillStyle: color, strokeStyle: color });
      staveNote.setLedgerLineStyle({ fillStyle: color, strokeStyle: color });
      if (note.duration === "8" || note.duration === "16") {
        staveNote.setFlagStyle({ fillStyle: color, strokeStyle: color });
      }

      if (!note.rest) {
        if (note.tieStop && tieState.pending) {
          if (tieState.pendingRow === row) {
            ties.push({ first: tieState.pending, last: staveNote });
          } else {
            // Tie spans a line break: draw the two halves separately.
            ties.push({ first: tieState.pending });
            ties.push({ last: staveNote });
          }
          tieState.pending = null;
        }
        if (note.tieStart) {
          tieState.pending = staveNote;
          tieState.pendingRow = row;
        }
      }

      vfNotes.push(staveNote);
    }

    return vfNotes;
  }, [noteStates, currentNoteIndex]);

  const drawVoice = useCallback((
    vfNotes: StaveNote[],
    stave: Stave,
    timeSignature: Exercise["timeSignature"],
    formatWidth: number
  ) => {
    if (vfNotes.length === 0) return;

    try {
      const beatsNum = parseInt(timeSignature.split("/")[0]);
      const beatVal = parseInt(timeSignature.split("/")[1]);
      const voice = new Voice({ num_beats: beatsNum, beat_value: beatVal });
      voice.setStrict(false);
      voice.addTickables(vfNotes);
      new Formatter().joinVoices([voice]).format([voice], Math.max(80, formatWidth));
      voice.draw(stave.getContext(), stave);
    } catch (err) {
      console.warn("VexFlow voice error:", err);
      vfNotes.forEach((n) => {
        try {
          n.setContext(stave.getContext());
          n.setStave(stave);
          n.draw();
        } catch {
          // no-op fallback for individual note draw failures
        }
      });
    }
  }, []);

  const renderNotation = useCallback(() => {
    if (!containerRef.current || !exercise) return;

    const container = containerRef.current;
    container.innerHTML = "";

    const containerWidth = container.clientWidth;
    if (containerWidth < 100) return;

    if (clef === "both") {
      const trebleMeasures = exercise.measures.filter((m) => m.clef === "treble");
      const bassMeasures = exercise.measures.filter((m) => m.clef === "bass");
      const measureCount = Math.min(trebleMeasures.length, bassMeasures.length);
      if (measureCount === 0) return;

      const measuresPerRow = Math.min(measureCount, Math.max(2, Math.floor(containerWidth / 260)));
      const staveWidth = Math.floor((containerWidth - 20) / measuresPerRow);
      const rows = Math.ceil(measureCount / measuresPerRow);
      const systemHeight = 190;
      const totalHeight = rows * systemHeight + 20;

      const renderer = new Renderer(container, Renderer.Backends.SVG);
      renderer.resize(containerWidth, totalHeight);
      const context = renderer.getContext();

      // Playable count excludes rests and tied continuations (merged into one playable note).
      const treblePlayableCount = trebleMeasures.reduce(
        (acc, measure) => acc + measure.notes.filter((note) => !note.rest && !note.tieStop).length,
        0
      );
      const trebleIndexRef = { value: 0 };
      const bassIndexRef = { value: treblePlayableCount };
      const trebleTieState: TieState = { pending: null, pendingRow: -1 };
      const bassTieState: TieState = { pending: null, pendingRow: -1 };
      const ties: TiePair[] = [];

      for (let row = 0; row < rows; row++) {
        const startMeasure = row * measuresPerRow;
        const endMeasure = Math.min(startMeasure + measuresPerRow, measureCount);
        const trebleY = row * systemHeight + 12;
        const bassY = trebleY + 84;

        for (let m = startMeasure; m < endMeasure; m++) {
          const trebleMeasure = trebleMeasures[m];
          const bassMeasure = bassMeasures[m];
          const x = (m - startMeasure) * staveWidth + 10;
          const isFirstInRow = m === startMeasure;
          const isFirstMeasure = m === 0;

          const trebleStave = new Stave(x, trebleY, staveWidth - 4);
          const bassStave = new Stave(x, bassY, staveWidth - 4);

          if (isFirstMeasure) {
            trebleStave.addClef("treble");
            trebleStave.addKeySignature(getVexFlowKeySignature(exercise.keySignature));
            trebleStave.addTimeSignature(exercise.timeSignature);
            bassStave.addClef("bass");
            bassStave.addKeySignature(getVexFlowKeySignature(exercise.keySignature));
            bassStave.addTimeSignature(exercise.timeSignature);
          } else if (isFirstInRow) {
            trebleStave.addClef("treble");
            bassStave.addClef("bass");
          }

          if (m === measureCount - 1) {
            trebleStave.setEndBarType(3);
            bassStave.setEndBarType(3);
          }

          trebleStave.setStyle({ strokeStyle: COLORS.staff, fillStyle: COLORS.staff });
          bassStave.setStyle({ strokeStyle: COLORS.staff, fillStyle: COLORS.staff });
          trebleStave.setContext(context).draw();
          bassStave.setContext(context).draw();

          if (isFirstInRow) {
            const brace = new StaveConnector(trebleStave, bassStave);
            brace.setType(StaveConnector.type.BRACE);
            brace.setContext(context).draw();

            const leftLine = new StaveConnector(trebleStave, bassStave);
            leftLine.setType(StaveConnector.type.SINGLE_LEFT);
            leftLine.setContext(context).draw();
          }

          const rightLine = new StaveConnector(trebleStave, bassStave);
          rightLine.setType(StaveConnector.type.SINGLE_RIGHT);
          rightLine.setContext(context).draw();

          const trebleNotes = buildVexFlowNotes(
            trebleMeasure.notes,
            "treble",
            exercise.keySignature,
            trebleIndexRef,
            trebleTieState,
            ties,
            row
          );
          const bassNotes = buildVexFlowNotes(
            bassMeasure.notes,
            "bass",
            exercise.keySignature,
            bassIndexRef,
            bassTieState,
            ties,
            row
          );

          const formatWidth = staveWidth - (isFirstMeasure ? 130 : isFirstInRow ? 65 : 35);
          drawVoice(trebleNotes, trebleStave, exercise.timeSignature, formatWidth);
          drawVoice(bassNotes, bassStave, exercise.timeSignature, formatWidth);
        }
      }

      drawTies(ties, context);
    } else {
      const measures = exercise.measures.filter((m) => m.clef === clef);
      if (measures.length === 0) return;

      const measuresPerRow = Math.min(measures.length, Math.max(2, Math.floor(containerWidth / 250)));
      const staveWidth = Math.floor((containerWidth - 20) / measuresPerRow);
      const rows = Math.ceil(measures.length / measuresPerRow);
      const rowHeight = 120;
      const totalHeight = rows * rowHeight + 20;

      const renderer = new Renderer(container, Renderer.Backends.SVG);
      renderer.resize(containerWidth, totalHeight);
      const context = renderer.getContext();

      const noteIndexRef = { value: 0 };
      const tieState: TieState = { pending: null, pendingRow: -1 };
      const ties: TiePair[] = [];

      for (let row = 0; row < rows; row++) {
        const startMeasure = row * measuresPerRow;
        const endMeasure = Math.min(startMeasure + measuresPerRow, measures.length);
        const y = row * rowHeight + 10;

        for (let m = startMeasure; m < endMeasure; m++) {
          const measure = measures[m];
          const x = (m - startMeasure) * staveWidth + 10;
          const isFirstInRow = m === startMeasure;
          const isFirstMeasure = m === 0;

          const stave = new Stave(x, y, staveWidth - 4);

          if (isFirstMeasure) {
            stave.addClef(clef);
            stave.addKeySignature(getVexFlowKeySignature(exercise.keySignature));
            stave.addTimeSignature(exercise.timeSignature);
          } else if (isFirstInRow) {
            stave.addClef(clef);
          }

          if (m === measures.length - 1) {
            stave.setEndBarType(3);
          }

          stave.setStyle({ strokeStyle: COLORS.staff, fillStyle: COLORS.staff });
          stave.setContext(context).draw();

          const vfNotes = buildVexFlowNotes(measure.notes, clef, exercise.keySignature, noteIndexRef, tieState, ties, row);
          const formatWidth = staveWidth - (isFirstMeasure ? 130 : isFirstInRow ? 65 : 35);
          drawVoice(vfNotes, stave, exercise.timeSignature, formatWidth);
        }
      }

      drawTies(ties, context);
    }

    // Style the SVG — white background
    const svg = container.querySelector("svg");
    if (svg) {
      svg.style.background = COLORS.background;
      svg.style.display = "block";
      svg.style.borderRadius = "6px";
    }
  }, [exercise, clef, buildVexFlowNotes, drawVoice]);

  useEffect(() => {
    renderNotation();

    const observer = new ResizeObserver(() => renderNotation());
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => observer.disconnect();
  }, [renderNotation]);

  return (
    <div
      ref={containerRef}
      className="w-full overflow-x-auto overflow-y-hidden rounded-md"
      style={{
        minHeight: 140,
        background: COLORS.background,
        border: "1px solid oklch(0.30 0.010 270)",
        boxShadow: "inset 0 1px 3px oklch(0 0 0 / 10%)",
      }}
    />
  );
}
